import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Briefcase, Plus, Trash, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployerDashboard() {
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    job_type: 'full-time',
    experience_level: 'mid',
    description: '',
    requirements: '',
    salary_range: ''
  });
  const { user } = useAuth();

  const fetchMyJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/jobs`, { withCredentials: true });
      const myJobs = data.filter(j => j.posted_by === user.id);
      setJobs(myJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }, [user.id]);

  useEffect(() => {
    fetchMyJobs();
  }, [fetchMyJobs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const jobData = {
        ...formData,
        requirements: formData.requirements.split('\\n').filter(r => r.trim())
      };
      
      await axios.post(`${API}/jobs`, jobData, { withCredentials: true });
      toast.success('Job posted successfully!');
      setShowForm(false);
      setFormData({
        title: '',
        location: '',
        job_type: 'full-time',
        experience_level: 'mid',
        description: '',
        requirements: '',
        salary_range: ''
      });
      fetchMyJobs();
    } catch (error) {
      toast.error('Failed to post job');
    }
  };

  const handleDelete = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      try {
        await axios.delete(`${API}/jobs/${jobId}`, { withCredentials: true });
        toast.success('Job deleted successfully');
        fetchMyJobs();
      } catch (error) {
        toast.error('Failed to delete job');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background_light">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl tracking-tight font-semibold mb-2">Employer Dashboard</h1>
            <p className="text-gray-600">Manage your job postings</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="toggle-job-form"
            className="flex items-center gap-2 bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
          >
            <Plus size={20} />
            Post a Job
          </button>
        </div>

        {showForm && (
          <div className="bg-white border border-borders rounded-sm p-8 mb-8">
            <h2 className="text-2xl tracking-tight font-medium mb-6">Create Job Posting</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Job Title</label>
                  <input
                    type="text"
                    data-testid="job-title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Pest Control Technician"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Location</label>
                  <input
                    type="text"
                    data-testid="job-location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    required
                    className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Pittsburgh, PA"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Job Type</label>
                  <select
                    data-testid="job-type"
                    value={formData.job_type}
                    onChange={(e) => setFormData({...formData, job_type: e.target.value})}
                    className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Experience Level</label>
                  <select
                    data-testid="job-experience"
                    value={formData.experience_level}
                    onChange={(e) => setFormData({...formData, experience_level: e.target.value})}
                    className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Salary Range (Optional)</label>
                <input
                  type="text"
                  value={formData.salary_range}
                  onChange={(e) => setFormData({...formData, salary_range: e.target.value})}
                  className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="$40,000 - $60,000"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Job Description</label>
                <textarea
                  data-testid="job-description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  rows="4"
                  className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Describe the position..."
                ></textarea>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Requirements (one per line)</label>
                <textarea
                  data-testid="job-requirements"
                  value={formData.requirements}
                  onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                  rows="4"
                  className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Valid driver's license\\nPest control experience\\nGood communication skills"
                ></textarea>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="submit-job"
                  className="bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                >
                  Post Job
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="bg-gray-200 text-text_dark px-6 py-3 rounded-sm hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">Your Job Postings ({jobs.length})</h2>
          
          {jobs.length === 0 ? (
            <div className="bg-white border border-borders rounded-sm p-8 text-center">
              <Briefcase size={64} className="mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">No job postings yet. Click &quot;Post a Job&quot; to get started.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {jobs.map(job => (
                <div key={job.id} className="bg-white border border-borders rounded-sm p-6" data-testid={`posted-job-${job.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl tracking-tight font-medium mb-2">{job.title}</h3>
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <MapPin size={16} />
                        <span>{job.location}</span>
                        <span>\u2022</span>
                        <span className="capitalize">{job.job_type.replace('-', ' ')}</span>
                        <span>\u2022</span>
                        <span className="uppercase">{job.experience_level}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(job.id)}
                      data-testid={`delete-job-${job.id}`}
                      className="text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Trash size={24} />
                    </button>
                  </div>
                  <p className="text-base leading-relaxed text-gray-700">{job.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
